import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const AndaContext = createContext(null);

const MIN_CHAT_WIDTH = 320;
const MAX_CHAT_WIDTH_RATIO = 0.5; // 50% of viewport

export function AndaProvider({ children }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatWidth, setChatWidth] = useState(400);
  const [resizing, setResizing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingImages, setStreamingImages] = useState([]);
  const [streamingThreadId, setStreamingThreadId] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;

  const openChat = useCallback(() => setChatOpen(true), []);
  const closeChat = useCallback(() => {
    setChatOpen(false);
    setHistoryOpen(false);
  }, []);
  const toggleHistory = useCallback(() => setHistoryOpen((v) => !v), []);
  const closeHistory = useCallback(() => setHistoryOpen(false), []);

  const startNewChat = useCallback(() => {
    setMessages([]);
    setThreadId(null);
    setStreamingContent('');
    setStreamingImages([]);
    setStreamingThreadId(null);
    setHistoryOpen(false);
  }, []);

  const addMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  const updateStreamingContent = useCallback((content) => {
    setStreamingContent(content);
  }, []);

  const addStreamingImage = useCallback((img) => {
    setStreamingImages((prev) => [...prev, img]);
  }, []);

  const finishStreaming = useCallback((fullText, messageId, streamThreadId, images = []) => {
    const currentThreadId = threadIdRef.current;
    if (fullText || images.length > 0) {
      setMessages((prev) => {
        if (streamThreadId != null && currentThreadId !== streamThreadId) {
          return prev;
        }
        let content;
        if (images.length > 0) {
          content = [{ type: 'text', content: fullText || '' }];
          images.forEach((img) => content.push({ type: 'image', data: img.data, mime_type: img.mime_type }));
        } else {
          content = fullText || '';
        }
        return [...prev, { role: 'assistant', content, id: messageId || `msg-${Date.now()}` }];
      });
    }
    setStreamingContent('');
    setStreamingImages([]);
    setIsStreaming(false);
    setStreamingThreadId(null);
  }, []);

  const value = {
    chatOpen,
    chatWidth,
    setChatWidth,
    resizing,
    setResizing,
    MIN_CHAT_WIDTH,
    MAX_CHAT_WIDTH_RATIO,
    historyOpen,
    messages,
    threadId,
    isStreaming,
    streamingContent,
    streamingImages,
    streamingThreadId,
    addStreamingImage,
    setStreamingThreadId,
    historyList,
    setChatOpen,
    setHistoryOpen,
    setMessages,
    setThreadId,
    setIsStreaming,
    setHistoryList,
    openChat,
    closeChat,
    toggleHistory,
    closeHistory,
    startNewChat,
    addMessage,
    updateStreamingContent,
    finishStreaming,
  };

  return <AndaContext.Provider value={value}>{children}</AndaContext.Provider>;
}

export function useAnda() {
  const ctx = useContext(AndaContext);
  if (!ctx) throw new Error('useAnda must be used within AndaProvider');
  return ctx;
}
